import { IconV1 } from 'common/icon/iconV1.js';
import { AdvertFuseModuleTargetingHook } from 'components/advert/ad-targeting/advert-targeting.js';
import { AdvertErrorBoundary } from 'components/advert/advert-error-boundary.js';
import { AdvertFuse } from 'components/advert/advert-fuse.js';
import { hasLegend } from 'components/legends/legend-types.js';
import { Legends } from 'components/legends/legends.js';
import { MarkerPillV2 } from 'components/map/marker-view/shared/pill-v2/marker-pill-v2.js';
import { ColumnLayoutContext } from 'page-context/column-layout/column-layout-context.js';
import { useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppSelector } from 'redux-state/configure-store.js';
import { IRepoState } from 'redux-state/rootReducer.js';
import { IMapKey, MapTags, MarkerDisplaySize } from 'types/location.js';
import { IMarker, getMarkerVersionPosition, predicateMarkerV2PillType } from 'types/markerCore/marker-core-type.js';
import { isNotNullOrUndefined, predicateMarkerV1 } from 'utilities/isType.js';
import { UseUpdateLocation } from 'utilities/router/router-updateLocation.js';
import { HTMLMarker } from '../../marker-view/shared/html/html-marker.js';

export interface IStaticMapProps extends MapTags {
  markers: IMarker[];
  keys?: IMapKey[];
}

/** Only use export for use in Storybook  */
export const StaticMapCore = ({ markers, keys, children, markerTags }: IStaticMapProps & { children: JSX.Element }) => {
  const isLarge = useContext(ColumnLayoutContext) !== '1-column';
  const navigate = useNavigate();
  const { onClick } = UseUpdateLocation();

  const nationalMarkers: IMarker[] = markers
    .map((marker) => {
      if (marker) {
        const staticPosition = getMarkerVersionPosition(marker).static;
        if (staticPosition?.left && staticPosition?.top) {
          return marker;
        }
      }
    })
    .filter(isNotNullOrUndefined);

  const showKey = hasLegend(keys);

  return (
    <div data-component="map-static" className="Map-container">
      {showKey && (
        <div className="Map-container-overlay">
          <Legends keys={keys} isRight />
        </div>
      )}

      <div className="Map Map--primary Map--static">
        <div className="Map-sponsorship" style={{ width: '100%', height: '100%' }}>
          {children}
        </div>

        <div className="Map-cartography">
          <div className="Map-cartography-image">
            <IconV1 icon="new-zealand-map" iconClass="Map-cartography-image-zIndex u-posRel" />
            {nationalMarkers.map((marker, i) => {
              if (predicateMarkerV1(marker)) {
                return <HTMLMarker key={i} marker={marker} isLarge={isLarge} navigate={navigate} />;
              }
              if (predicateMarkerV2PillType(marker)) {
                return (
                  <MarkerPillV2
                    marker={marker}
                    markerDisplaySize={MarkerDisplaySize.Large}
                    markerThemes={markerTags}
                    onClick={() => onClick(marker)}
                  />
                );
              }
              return null;
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export const StaticMap = (props: IStaticMapProps) => {
  const mapTakeover = useAppSelector((state: IRepoState) => state.ADVERTISEMENT.mapTakeover);

  const finalTargeting = AdvertFuseModuleTargetingHook({
    overrideBaseTargeting: false,
    mergeBaseTargeting: false,
    targeting: mapTakeover?.targeting,
  });

  return (
    <StaticMapCore {...props}>
      <div className="Map-sponsorship" style={{ width: '100%', height: '100%' }}>
        {mapTakeover && (
          <AdvertErrorBoundary>
            <AdvertFuse
              fuseTag={mapTakeover.fuseTag}
              adUnitPath={mapTakeover.adUnitPath}
              targeting={finalTargeting}
              origin={'adv-MapTakeover'}
            />
          </AdvertErrorBoundary>
        )}
      </div>
    </StaticMapCore>
  );
};
